import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from '@3plearning/react-components';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import isEqual from 'lodash/isEqual';
import css from './report-filters.scss';
import {
    GROUP_LIST,
    REPORT_LIST,
    SELECTED_DATE,
    SELECTED_REPORT,
    SKILL_QUESTS_MASTERY,
    STUDENT_LIST
} from './filter-constants';
import activeFiltersActions from './redux/active-filters/active-filters-actions';
import RouteHelper from '../../helpers/route-helper';
import {
    activeFiltersPropType,
    studentsByGroupPropType,
    classPropType,
    yearGroupPropType,
    teacherPropType,
    filterPropType
} from './filter-prop-types';
import academicYearActions from './redux/academic-year/academic-year-actions';
import allClassesActions from './redux/class-list/class-list-actions';
import studentsByGroupActions from './redux/students-by-group/students-by-group-actions';
import hasLifetimeDataActions from './redux/has-lifetime-data/has-lifetime-data-actions';
import dropdownDataActions from './redux/dropdown-data/dropdown-data-actions';
import yearGroupsActions from './redux/year-groups/year-groups-actions';
import studentsByGroupSelectors from './redux/students-by-group/students-by-group-selectors';
import yearGroupsSelectors from './redux/year-groups/year-groups-selectors';
import isAdminActions from '../../components/report-filters/redux/is-admin/is-admin-actions';
import {
    ConnectedYearGroupDropdown,
    ConnectedTeacherDropdown,
    ConnectedClassDropdown,
    ConnectedGroupDropdown,
    ConnectedStudentDropdown,
    ConnectedReportDropdown,
    ConnectedDateDropdown
} from './dropdown-menus';
import dropdownDataSelectors from './redux/dropdown-data/dropdown-data-selectors';

/**
 * ReportFilters component - displays various dropdowns for filtering purposes
 * After selecting filters user clicks on Search button
 *
 * Filters have complex logic related to things like auto-selecting based on page route,
 * disabling if only one option, etc.
 */

export class ReportFilters extends Component {
    static contextTypes = {
        router: PropTypes.object
    };

    static propTypes = {
        activeFilters: activeFiltersPropType,
        reportDropdownData: PropTypes.shape({
            selectedOption: filterPropType
        }),
        academicYear: PropTypes.shape({
            startDate: PropTypes.string,
            endDate: PropTypes.string
        }),
        allClasses: PropTypes.arrayOf(classPropType),
        yearGroups: PropTypes.arrayOf(yearGroupPropType),
        teachers: PropTypes.arrayOf(teacherPropType),
        studentsByGroup: PropTypes.arrayOf(studentsByGroupPropType),
        isAdmin: PropTypes.bool,
        setActiveFilters: PropTypes.func,
        setFiltersFromSelectedOptions: PropTypes.func,
        setSelectedOptionsFromFilters: PropTypes.func,
        getIsAdmin: PropTypes.func,
        getAcademicYear: PropTypes.func,
        getAllClassesData: PropTypes.func,
        getStudentsByGroup: PropTypes.func,
        getHasLifetimeData: PropTypes.func,
        setDropdownData: PropTypes.func,
        getYearGroups: PropTypes.func,
        setSelectedDate: PropTypes.func
    };

    constructor(props, context) {
        super(props, context);

        const { history } = context.router;
        this.state = {
            route: history.location.pathname,
            params: history.location.search,
            hasLoaded: false
        };
    }

    componentDidMount() {
        const {
            getAcademicYear,
            getAllClassesData,
            getHasLifetimeData,
            getIsAdmin
        } = this.props;

        getIsAdmin();
        getAcademicYear();
        getHasLifetimeData();
        getAllClassesData();
        this.listenForBrowserBack();
    }

    componentWillReceiveProps(newProps) {
        const {
            academicYear,
            activeFilters,
            isAdmin,
            studentsByGroup,
            allClasses,
            teachers,
            yearGroups,
            getStudentsByGroup,
            getYearGroups
        } = this.props;
        const {
            academicYear: newAcademicYear,
            activeFilters: newActiveFilters,
            isAdmin: newIsAdmin,
            studentsByGroup: newStudentsByGroup,
            allClasses: newAllClasses,
            teachers: newTeachers,
            yearGroups: newYearGroups
        } = newProps;
        const { route: prevRoute, params: prevParams } = this.state;
        const { location } = this.context.router.history;

        const route = RouteHelper.calculateRouteFromFilters(newActiveFilters);
        const params = RouteHelper.calculateParamsFromFilters(newActiveFilters);
        const selectedClass = RouteHelper.calculateSelectedClassFromRoute(
            location.pathname,
            newAllClasses
        );
        const hasActiveFiltersSelectedDateAvailable =
            activeFilters &&
            activeFilters.selectedDate &&
            activeFilters.selectedDate.startDate === null &&
            activeFilters.selectedDate.endDate === null &&
            newActiveFilters &&
            newActiveFilters.selectedDate &&
            newActiveFilters.selectedDate.startDate !== null &&
            newActiveFilters.selectedDate.endDate !== null;
        const hasAcademicYearAvailable =
            academicYear &&
            academicYear.startDate === null &&
            academicYear.endDate === null &&
            newAcademicYear &&
            newAcademicYear.startDate !== null &&
            newAcademicYear.endDate !== null;

        if (hasActiveFiltersSelectedDateAvailable) {
            this.appendAcademicYear(params);
        }

        if (hasAcademicYearAvailable) {
            const overrideParams = RouteHelper.calculateParamsFromFilters({
                [SELECTED_DATE]: newAcademicYear
            });

            this.appendAcademicYear(overrideParams);
        }

        /** Loads the new route when the active filters change */
        if (
            activeFilters !== newActiveFilters &&
            (route !== prevRoute ||
                RouteHelper.areDateParamsDifferent(params, prevParams))
        ) {
            this.loadNewRoute(route, params, newActiveFilters);
        }

        /** Load admin dropdown data */
        if (isAdmin !== newIsAdmin && newIsAdmin === true) {
            getYearGroups();
        }

        /** Loads groups + students for selected class on first load */
        if (allClasses !== newAllClasses) {
            if (selectedClass) {
                getStudentsByGroup(selectedClass.id);
            } else {
                this.filterDataLoaded(newProps, selectedClass);
            }
        }

        if (
            allClasses !== newAllClasses ||
            studentsByGroup !== newStudentsByGroup ||
            yearGroups !== newYearGroups ||
            teachers !== newTeachers ||
            isAdmin !== newIsAdmin
        ) {
            this.filterDataLoaded(newProps, selectedClass);
        }
    }

    componentWillUnmount() {
        if (this.unlisten) {
            this.unlisten();
        }
    }

    filterDataLoaded(props, selectedClass) {
        if (!this.state.hasLoaded) {
            const {
                yearGroups,
                teachers,
                allClasses,
                studentsByGroup,
                isAdmin
            } = props;

            let allFiltersLoaded;
            const isClassEmpty = studentsByGroup.length === 0;
            if (isAdmin === true) {
                allFiltersLoaded =
                    teachers.length > 0 &&
                    yearGroups.length > 0 &&
                    allClasses.length > 0 &&
                    (!selectedClass || studentsByGroup.length > 0);
            } else if (isAdmin === false) {
                allFiltersLoaded =
                    allClasses.length > 0 &&
                    (!selectedClass || studentsByGroup.length > 0);
            }

            if (allFiltersLoaded || isClassEmpty) {
                const { location } = this.context.router.history;
                setTimeout(() => {
                    this.setFiltersFromRoute(
                        location.pathname,
                        location.search,
                        props
                    );
                }, 0);
            }
        }
    }

    /**
     * Updates the filters when user clicks on browser's back button
     */
    listenForBrowserBack() {
        const { history } = this.context.router;
        const { setDropdownData } = this.props;

        this.unlisten = history.listen((location, action) => {
            if (
                location.pathname !== this.state.route ||
                location.search !== this.state.params
            ) {
                this.setState({ hasLoaded: false });
            }
            if (action === 'POP') {
                const { activeFilters, allClasses } = this.props;

                // clear group/student dropdowns if returning to all classes view
                const selectedClass = RouteHelper.calculateSelectedClassFromRoute(
                    location.pathname,
                    allClasses
                );
                if (!selectedClass) {
                    setDropdownData({
                        [GROUP_LIST]: { data: [] },
                        [STUDENT_LIST]: { data: [] }
                    });
                    this.filterDataLoaded(this.props);
                } else {
                    this.loadGroupsAndStudentsFromRoute(
                        location.pathname,
                        location.search,
                        activeFilters
                    );
                }
            }
        });
    }

    /**
     * Runs when active filters have changed and we need to load a new page in the app
     * Pushes new route into history, and sets the new state of the dropdown menus - disabled,
     * selected options, loading group/student data, etc.
     */
    loadNewRoute(route, params) {
        const { history } = this.context.router;

        history.push({ pathname: route, search: params });
        this.loadGroupsAndStudentsFromRoute(route, params);
    }

    appendAcademicYear(params) {
        const { history } = this.context.router;

        history.push({ search: params });
    }

    /**
     * Loads the groups and students for the class in the route
     * If there is no class in the route, will just proceed to setting the filters using the route
     */
    loadGroupsAndStudentsFromRoute(route, params) {
        const { allClasses, getStudentsByGroup } = this.props;
        const selectedClass = RouteHelper.calculateSelectedClassFromRoute(
            route,
            allClasses
        );
        if (selectedClass) {
            const madeRequest = getStudentsByGroup(selectedClass.id);
            // if the class id has not changed, we will not make a new request
            // for group + student data so need to set the filters here
            if (madeRequest) {
                this.setState({ hasLoaded: false });
            } else {
                this.setFiltersFromRoute(route, params, this.props);
            }
        } else {
            this.setFiltersFromRoute(route, params, this.props);
        }
    }

    /**
     * Sets the active filters by checking the route
     * Used on initial app load if user loads a child route ie. activity-mastery/class/187
     * and if user navigates by clicking on items in the table ie. a class in all classes table
     */
    setFiltersFromRoute(route, params, props) {
        const {
            activeFilters,
            yearGroups,
            teachers,
            allClasses,
            studentsByGroup
        } = props;
        const { setSelectedOptionsFromFilters, setActiveFilters } = this.props;

        const filtersFromRoute = RouteHelper.calculateFiltersFromRoute(
            route,
            params,
            allClasses,
            studentsByGroup,
            yearGroups,
            teachers,
            activeFilters
        );

        this.setState({ route, params, hasLoaded: true });
        setSelectedOptionsFromFilters(filtersFromRoute);
        if (!isEqual(activeFilters, filtersFromRoute)) {
            setActiveFilters(filtersFromRoute);
        }
    }

    applyFilters = () => {
        this.setState({ hasLoaded: false });
        this.manageFilterOverride();
        this.props.setFiltersFromSelectedOptions();
    };

    /**
     * Business rule for Skill Quests Mastery report that custom date start date needs to be start of
     * academic year... edge case exists when navigating from any report type to Skill Quests Mastery
     * that pre selected custom date will conflict with this rule... hence we create override function to default
     * Skills Quests Mastery report to 'This school year' everytime :(
     */
    manageFilterOverride = () => {
        const {
            activeFilters: {
                [SELECTED_REPORT]: { id: selectedReportId }
            },
            reportDropdownData: {
                selectedOption: { value: dropdownReportId }
            },
            academicYear
        } = this.props;

        if (
            dropdownReportId === SKILL_QUESTS_MASTERY &&
            dropdownReportId !== selectedReportId
        ) {
            this.props.setSelectedDate({
                type: DatePicker.THIS_SCHOOL_YEAR,
                startDate: academicYear.startDate,
                endDate: academicYear.endDate
            });
        }
    };

    render() {
        const { isAdmin } = this.props;

        return (
            <div className={css['report-filter-container']}>
                {isAdmin && [
                    <ConnectedYearGroupDropdown
                        key={0}
                        className={css.dropdown}
                        applyFilters={this.applyFilters}
                    />,
                    <ConnectedTeacherDropdown
                        key={1}
                        className={css.dropdown}
                        applyFilters={this.applyFilters}
                    />
                ]}
                <ConnectedClassDropdown
                    className={css.dropdown}
                    handleClassChange={this.applyFilters}
                />
                <ConnectedGroupDropdown
                    className={css.dropdown}
                    handleGroupChange={this.applyFilters}
                />
                <ConnectedStudentDropdown
                    className={css.dropdown}
                    handleStudentChange={this.applyFilters}
                />
                <ConnectedReportDropdown
                    className={css.dropdown}
                    handleApplyFilters={this.applyFilters}
                />
                <ConnectedDateDropdown
                    className={css['report-date-filter']}
                    handleApplyFilters={this.applyFilters}
                />
            </div>
        );
    }
}

/*
 * mapStateToProps - set component properties from current state provided by the Reducers.
 * @param state {object} - the revised state from CombinedReducers.
 */
function mapStateToProps(state) {
    return {
        academicYear: state.academicYear,
        activeFilters: state.activeFilters,
        reportDropdownData: dropdownDataSelectors.selectDropdownDataByType(
            REPORT_LIST
        )(state),
        yearGroups: yearGroupsSelectors.selectYearGroups(state),
        teachers: yearGroupsSelectors.selectAllTeachers(state),
        allClasses: state.allClasses,
        studentsByGroup: studentsByGroupSelectors.selectGroupsAndStudents(
            state
        ),
        isAdmin: state.isAdmin
    };
}

/*
 * mapDispatchToProps - ensure that the outputs of the Action Creators are passed to the Reducers
 * @param dispatch
 */
function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            setActiveFilters: activeFiltersActions.setActiveFilters,
            setFiltersFromSelectedOptions:
                activeFiltersActions.setFiltersFromSelectedOptions,
            getAcademicYear: academicYearActions.getAcademicYear,
            getAllClassesData: allClassesActions.getClassesData,
            getStudentsByGroup: studentsByGroupActions.getStudentsByGroup,
            getHasLifetimeData: hasLifetimeDataActions.getHasLifetimeData,
            setDropdownData: dropdownDataActions.setDropdownData,
            setSelectedOptionsFromFilters:
                dropdownDataActions.setSelectedOptionsFromFilters,
            getYearGroups: yearGroupsActions.getYearGroups,
            getIsAdmin: isAdminActions.getIsSchoolAdmin,
            setSelectedDate: dropdownDataActions.setSelectedDate
        },
        dispatch
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ReportFilters);



// WEBPACK FOOTER //
// ./components/report-filters/report-filters.js